'use strict';

// Single source of truth for whether a food is "ready" to be used as a
// recipe ingredient. Mirrors recipe/src/Chewba/Utils/FoodReadyState.php —
// keep the two in sync.
//
// Each rule emits a flag keyed by a stable KEY_* string and classified
// as HARD (blocks usage; corrupts the solver) or WARNING (lower-signal).

// ─── Tolerance constants ───
const ATWATER_THRESHOLD = 0.15;
// Atwater check is skipped below this floor — at low kcal the signal-to-noise
// ratio gets too high and the check fires alarmingly on benign rounding.
const ATWATER_MIN_CAL = 50.0;
const ENERGY_KJ_PER_KCAL = 4.184;
const ENERGY_KJ_TOLERANCE = 0.01;
const MASS_BALANCE_TOL_FRAC = 0.05;
const MASS_BALANCE_MIN_MASS_G = 50.0;
const TREE_SUM_TOL = 0.03;
const FIBER_SPLIT_TOL = 0.10;
const FAT_TREE_MIN_RATIO = 0.80;
const AMINO_MIN_RATIO = 0.70;
const FAT_TREE_MIN_PARENT_G = 0.5;
const VIT_A_TOL = 0.20;
const VIT_D_IU_PER_MCG = 40.0;
const VIT_D_TOL = 0.05;
const FLUIDS_WATER_TOL = 0.20;
const CALORIES_MAX = 5000;
const SODIUM_MAX = 10000;
const CAL_PER_GRAM_MAX = 9.5;
const FRU_PER_GRAM_MAX = 0.2;
const VEG_PER_GRAM_MAX = 0.2;
const BOUND_G_PER_G = 1.0;
const BOUND_MG_PER_G = 1000.0;
const BOUND_UG_PER_G = 1000000.0;

// ─── nutrNo groups ───
const INDIVIDUAL_SFAS = [
    '607', '608', '609', '610', '611', '612', '613', '614', '615',
    '624', '652', '653', '654', '696',
];
const INDIVIDUAL_MUFAS = [
    '617',
    '625', '626', '628', '630',
    '671',
    '687', '697',
];
const INDIVIDUAL_PUFAS = [
    '618', '619', '620',
    '621', '629', '631',
    '627',
    '670',
    '672', '685',
    '689', '853',
];
const OMEGA3_SPECIES = ['621', '629', '631', '627', '851', '852'];
const OMEGA6_SPECIES = ['672', '675', '685', '853', '855'];
const TRANS_COMPONENTS = ['693', '695'];
const AMINO_ACIDS = [
    '501', '502', '503', '504', '505', '506', '507', '508', '509',
    '510', '511', '512', '513', '514', '515', '516', '517', '518',
];
const SUGAR_COMPONENTS = ['210', '211', '212', '213', '214', '287'];
const TOCOPHEROL_TOCOTRIENOL = ['341', '342', '343', '344', '345', '346', '347'];
const MINERALS_MG = ['301', '303', '304', '305', '306', '307', '309', '312', '315'];
const MINERALS_UG = ['317', 'IOD', 'MBD', 'CHR'];

export const LABEL_NUTRIENTS = [
    '208', '204', '606', '605', '601',
    '307', '205', '291', '269', '203',
    '303', '306', '301',
];

const FODMAP_NUTRIENTS = ['OLF', 'OLG', 'FRC', 'LAC', 'SOR', 'MAN'];

const G_UNIT_NUTRIENTS = [
    '203', '204', '205', '207', '209', '210', '211', '212', '213', '214',
    '221', '255', '257', '269', '287', '291', '605', '606', '645', '646',
    'ASG', 'SAL', 'SFB', 'IFB',
];

const ALLOW_NEGATIVE = ['RAL'];

// ─── Sanity flag keys ───
export const KEY_CALORIES = 'implausible calories';
export const KEY_SODIUM = 'implausible sodium';
export const KEY_MACROS = 'implausible macros';
export const KEY_CALORIE_DENSITY = 'implausible calorie density';
export const KEY_FRUIT_DENSITY = 'implausible fruit density';
export const KEY_VEGGIE_DENSITY = 'implausible veggie density';

export const KEY_ENERGY_KJ = 'energy kJ mismatch';
export const KEY_MASS_BALANCE = 'mass balance off';
export const KEY_NEGATIVE = 'negative nutrient';
export const KEY_OVERWEIGHT = 'nutrient exceeds weight';
export const KEY_UNIT_OUTLIER = 'unit outlier';

export const KEY_CARB_TREE = 'carb tree inconsistent';
export const KEY_SUGAR_COMPOSITION = 'sugar composition inconsistent';
export const KEY_FIBER_SPLIT = 'fiber split inconsistent';
export const KEY_FAT_TREE = 'fat tree inconsistent';
export const KEY_SFA_SUM = 'saturated FA sum inconsistent';
export const KEY_MUFA_SUM = 'MUFA sum inconsistent';
export const KEY_PUFA_SUM = 'PUFA sum inconsistent';
export const KEY_TRANS_SUM = 'trans FA sum inconsistent';
export const KEY_OMEGA_CONSISTENCY = 'omega inconsistent';
export const KEY_AMINO_ACIDS = 'amino acids inconsistent';
export const KEY_STEROL_SUM = 'sterol sum inconsistent';
export const KEY_FODMAP = 'fodmap inconsistent';
export const KEY_VIT_E_TREE = 'vitamin E tree inconsistent';
export const KEY_FLUIDS_WATER = 'fluids water mismatch';
export const KEY_SERVINGS_HIERARCHY = 'servings hierarchy inconsistent';
export const KEY_MINERALS_ASH = 'minerals exceed ash';

export const KEY_VIT_D_UNITS = 'vitamin D unit mismatch';
export const KEY_VIT_D_SPLIT = 'vitamin D split inconsistent';
export const KEY_VIT_A_UNITS = 'vitamin A unit mismatch';
export const KEY_FOLATE = 'folate inconsistent';

// ─── Missing-data flag keys ───
export const KEY_MISSING_CALORIES = 'calories';
export const KEY_PRICE = 'price';
export const KEY_DENSITY = 'density';
export const KEY_LABEL_NUTRIENTS = 'label nutrients';
export const KEY_BRAND = 'brand';
export const KEY_SERVING_UNIT = 'serving unit';
export const KEY_SERVING_DESCRIPTION = 'serving description';
export const KEY_GRAMS_SERVING = 'grams/serving';
export const KEY_ML_SERVING = 'ml/serving';
export const KEY_INGREDIENTS_LIST = 'ingredients list';
export const KEY_CATEGORY = 'category';
export const KEY_UNITS = 'units';
export const KEY_DEFAULT_UNIT = 'default unit';
export const KEY_PACKAGING = 'packaging';
export const KEY_BARCODE = 'barcode';
export const KEY_FODMAPS = 'fodmaps';
export const KEY_ADDED_SUGAR = 'added sugar';
export const KEY_UGC = 'ugc';

// External flags emitted by other parts of the system but classified here:
export const KEY_REJECTED = 'rejected';
export const KEY_MAPPING = 'mapping';
export const KEY_INGREDIENT_AMOUNT = 'ingredient amount';
export const KEY_NOT_VERIFIED = 'not verified';
export const KEY_BRAND_NAME_FOOD = 'brand name food';

// ─── Severity classification ───
// HARD: blocks usage as a recipe ingredient OR corrupts the solver.
// Anything not listed here is treated as a WARNING.

export const HARD_KEYS = new Set([
    KEY_CALORIES,
    KEY_SODIUM,
    KEY_MACROS,
    KEY_CALORIE_DENSITY,
    KEY_FRUIT_DENSITY,
    KEY_VEGGIE_DENSITY,
    KEY_MASS_BALANCE,
    KEY_NEGATIVE,
    KEY_OVERWEIGHT,
    KEY_UNIT_OUTLIER,
    KEY_CARB_TREE,
    KEY_MISSING_CALORIES,
    KEY_UGC,
    KEY_LABEL_NUTRIENTS,
    KEY_SERVING_UNIT,
    KEY_GRAMS_SERVING,
    KEY_ML_SERVING,
    KEY_REJECTED,
    KEY_MAPPING,
    KEY_INGREDIENT_AMOUNT,
]);

export function isHardKey(key) {
    return HARD_KEYS.has(key);
}

export function isWarningKey(key) {
    return !HARD_KEYS.has(key);
}

// ─── Shared primitives ───

function v(values, code) {
    return Number(values?.[code] ?? 0);
}

function sumCodes(values, codes) {
    let sum = 0.0;
    for (const c of codes) {
        sum += v(values, c);
    }
    return sum;
}

function anyAvailable(available, codes) {
    if (!available) return false;
    for (const c of codes) {
        if (available[c]) return true;
    }
    return false;
}

function classify(flags) {
    const hard = {};
    const warnings = {};
    for (const [key, msg] of Object.entries(flags)) {
        if (HARD_KEYS.has(key)) {
            hard[key] = msg;
        } else {
            warnings[key] = msg;
        }
    }
    return { hard, warnings };
}

// ─── Public API ───

/**
 * Evaluate a food's full ready-state. Returns hard issues + warnings,
 * each as a key=>message map.
 */
export function evaluate(food) {
    const flags = {
        ...checkMissingData(food),
        ...checkSanity(food),
    };
    return classify(flags);
}

/**
 * Sanity-only variant — runs only the nutrient-value rules.
 */
export function evaluateSanity(food) {
    return classify(checkSanity(food));
}

/**
 * Per-gram variant — used to flag a mapped food's underlying nutrient
 * profile when it looks broken.
 */
export function evaluatePerGram(nutrientsPerGram) {
    const flags = {
        ...checkDensitiesPerGram(nutrientsPerGram),
        ...runRules(nutrientsPerGram, 1.0),
    };
    return classify(flags);
}

// ─── Missing-data rules ───

function checkMissingData(food) {
    const flags = {};
    const tags = food?.tags || [];
    const isMissingFoodLabel = tags.includes('Missing Food Label');
    const isMissingPackaging = tags.includes('Missing Packaging');
    const isMissingDensity = tags.includes('Missing Density');
    const available = food?.nutrients?.available || {};

    if (tags.includes('Rejected')) {
        flags[KEY_REJECTED] = 'Food is tagged Rejected.';
    }

    if (!available['208']) {
        flags[KEY_MISSING_CALORIES] = 'Calories (208) is not populated.';
    }

    if (food?.product_type === 'UGC') {
        flags[KEY_UGC] = 'Food is User-Generated Content; not trusted.';
    }

    if (!food?.standard_price) {
        flags[KEY_PRICE] = 'No standard price set.';
    }

    if (isMissingDensity) {
        flags[KEY_DENSITY] = 'Density could not be computed.';
    }

    for (const num of LABEL_NUTRIENTS) {
        if (!available[num]) {
            flags[KEY_LABEL_NUTRIENTS] = 'One or more FDA label nutrients missing.';
            break;
        }
    }

    const productType = food?.product_type || '';

    if (['Restaurant Dish', 'Ready Made Meal', 'Meal Kit'].includes(productType)) {
        if (!food.brand_uuid) flags[KEY_BRAND] = 'No brand selected.';
        if (!food.serving_unit) flags[KEY_SERVING_UNIT] = 'No serving unit set.';
        if (!isMissingFoodLabel && !food.serving_description) {
            flags[KEY_SERVING_DESCRIPTION] = 'No human-readable serving description.';
        }
        if (!isMissingFoodLabel && food.serving_unit == 'g' && !food.grams_per_serving) {
            flags[KEY_GRAMS_SERVING] = 'No grams per serving.';
        }
        if (!isMissingFoodLabel && food.serving_unit == 'ml' && !food.milliliters_per_serving) {
            flags[KEY_ML_SERVING] = 'No milliliters per serving.';
        }
        if (!food.category) flags[KEY_CATEGORY] = 'No grocery category.';
    } else if (productType === 'Brand Name Food') {
        if (!food.brand_uuid) flags[KEY_BRAND] = 'No brand selected.';
        if (!food.serving_unit) flags[KEY_SERVING_UNIT] = 'No serving unit set.';
        if (!isMissingFoodLabel && !food.serving_description) {
            flags[KEY_SERVING_DESCRIPTION] = 'No human-readable serving description.';
        }
        if (!isMissingFoodLabel && food.serving_unit == 'g' && !food.grams_per_serving) {
            flags[KEY_GRAMS_SERVING] = 'No grams per serving.';
        } else if (!isMissingFoodLabel && food.serving_unit == 'ml' && !food.milliliters_per_serving) {
            flags[KEY_ML_SERVING] = 'No milliliters per serving.';
        }
        if (!food.units?.length) flags[KEY_UNITS] = 'No unit options defined.';
        else if (!food.default_unit) flags[KEY_DEFAULT_UNIT] = 'No default unit selected.';
        if (!food.category) flags[KEY_CATEGORY] = 'No grocery category.';
        if (!isMissingPackaging && !food.packaging?.length) {
            flags[KEY_PACKAGING] = 'No packaging defined.';
        }
        if (!food.gtin_upc?.length) flags[KEY_BARCODE] = 'No UPC/GTIN.';
        if (!anyAvailable(available, FODMAP_NUTRIENTS)) {
            flags[KEY_FODMAPS] = 'No FODMAP-relevant nutrients populated.';
        }
    } else if (productType === 'Food') {
        if (!food.serving_unit) flags[KEY_SERVING_UNIT] = 'No serving unit set.';
        if (food.serving_unit == 'g' && !food.grams_per_serving) {
            flags[KEY_GRAMS_SERVING] = 'No grams per serving.';
        }
        if (food.serving_unit == 'ml' && !food.milliliters_per_serving) {
            flags[KEY_ML_SERVING] = 'No milliliters per serving.';
        }
        if (!food.units?.length) flags[KEY_UNITS] = 'No unit options defined.';
        else if (!food.default_unit) flags[KEY_DEFAULT_UNIT] = 'No default unit selected.';
        if (!food.category) flags[KEY_CATEGORY] = 'No grocery category.';
        if (!available['ASG'] && available['269']) {
            flags[KEY_ADDED_SUGAR] = 'Total sugar is reported but added sugar is not.';
        }
        if (!anyAvailable(available, FODMAP_NUTRIENTS)) {
            flags[KEY_FODMAPS] = 'No FODMAP-relevant nutrients populated.';
        }
        if (!isMissingPackaging && !food.packaging) {
            flags[KEY_PACKAGING] = 'No packaging defined.';
        }
    }

    return flags;
}

// ─── Sanity rules ───

function checkSanity(food) {
    const values = food?.nutrients?.values ?? {};
    const gps = Number(food?.grams_per_serving ?? 0);
    const cals = v(values, '208');
    const sodium = v(values, '307');

    let flags = {};

    if (cals > CALORIES_MAX) {
        flags[KEY_CALORIES] = `Absurd calories: ${cals.toFixed(0)} kcal`;
    }

    if (sodium > SODIUM_MAX) {
        flags[KEY_SODIUM] = `Absurd sodium: ${sodium.toFixed(0)}mg`;
    }

    if (gps > 0) {
        flags = { ...flags, ...checkDensities(values, gps) };
    }

    flags = { ...flags, ...runRules(values, gps > 0 ? gps : null) };

    return flags;
}

function runRules(values, refMass) {
    const flags = {};
    const checks = [
        checkAtwater(values),
        checkEnergyKj(values),
        checkNegative(values),
        checkOverweight(values, refMass),
        checkUnitOutlier(values, refMass),
        checkMassBalance(values, refMass),
        checkCarbTree(values),
        checkSugarComposition(values),
        checkFiberSplit(values),
        checkFatTree(values),
        checkFatSubSum(values, '606', INDIVIDUAL_SFAS, KEY_SFA_SUM, 'saturated'),
        checkFatSubSum(values, '645', INDIVIDUAL_MUFAS, KEY_MUFA_SUM, 'MUFA'),
        checkFatSubSum(values, '646', INDIVIDUAL_PUFAS, KEY_PUFA_SUM, 'PUFA'),
        checkTransSum(values),
        checkOmegaConsistency(values),
        checkAminoAcids(values),
        checkSterolSum(values),
        checkFodmap(values),
        checkVitETree(values),
        checkFluidsWater(values),
        checkServingsHierarchy(values),
        checkMineralsAsh(values),
        checkVitDUnits(values),
        checkVitDSplit(values),
        checkVitAUnits(values),
        checkFolate(values),
    ];
    for (const result of checks) {
        if (result !== null) {
            flags[result[0]] = result[1];
        }
    }
    return flags;
}

// ─── Density caps ───

function checkDensities(values, gps) {
    const flags = {};
    const cals = v(values, '208');
    const fru = v(values, 'FRU');
    const veg = v(values, 'VEG');

    if (cals / gps > CAL_PER_GRAM_MAX) {
        flags[KEY_CALORIE_DENSITY] = `Absurd calorie density: ${(cals / gps).toFixed(1)} kcal/g`;
    }
    if (fru / gps > FRU_PER_GRAM_MAX) {
        flags[KEY_FRUIT_DENSITY] = `Absurd fruit density: ${(fru / gps).toFixed(2)} FRU/g`;
    }
    if (veg / gps > VEG_PER_GRAM_MAX) {
        flags[KEY_VEGGIE_DENSITY] = `Absurd veggie density: ${(veg / gps).toFixed(2)} VEG/g`;
    }
    return flags;
}

function checkDensitiesPerGram(values) {
    const flags = {};
    const cal = v(values, '208');
    const fru = v(values, 'FRU');
    const veg = v(values, 'VEG');

    if (cal > CAL_PER_GRAM_MAX) flags[KEY_CALORIE_DENSITY] = `${cal.toFixed(1)} kcal/g`;
    if (fru > FRU_PER_GRAM_MAX) flags[KEY_FRUIT_DENSITY] = `${fru.toFixed(2)} FRU/g`;
    if (veg > VEG_PER_GRAM_MAX) flags[KEY_VEGGIE_DENSITY] = `${veg.toFixed(2)} VEG/g`;
    return flags;
}

// ─── Tier 1 rules ───

function checkAtwater(values) {
    const cals = v(values, '208');
    if (cals <= ATWATER_MIN_CAL) return null;
    const implied = 4 * v(values, '203')
        + 9 * v(values, '204')
        + 4 * v(values, '205')
        + 7 * v(values, '221');
    if (implied <= 0) return null;
    if (Math.abs(implied - cals) / cals > ATWATER_THRESHOLD) {
        return [KEY_MACROS, `Macro/calorie mismatch: implied=${implied.toFixed(1)} kcal vs stated=${cals.toFixed(1)} kcal`];
    }
    return null;
}

function checkEnergyKj(values) {
    const cals = v(values, '208');
    const kj = v(values, '268');
    if (cals <= 0 || kj <= 0) return null;
    const expected = cals * ENERGY_KJ_PER_KCAL;
    if (Math.abs(kj - expected) / expected > ENERGY_KJ_TOLERANCE) {
        return [KEY_ENERGY_KJ, `Energy kJ/kcal mismatch: stated=${kj.toFixed(1)} vs expected=${expected.toFixed(1)}`];
    }
    return null;
}

function checkNegative(values) {
    for (const code of Object.keys(values || {})) {
        if (ALLOW_NEGATIVE.includes(String(code))) continue;
        if (Number(values[code]) < 0) {
            return [KEY_NEGATIVE, `Negative value for ${code}: ${Number(values[code]).toFixed(3)}`];
        }
    }
    return null;
}

function checkOverweight(values, refMass) {
    if (refMass === null || refMass <= 0) return null;
    for (const code of G_UNIT_NUTRIENTS) {
        if (v(values, code) > refMass) {
            return [KEY_OVERWEIGHT, `Nutrient ${code}=${v(values, code).toFixed(2)}g exceeds ${refMass.toFixed(2)}g`];
        }
    }
    return null;
}

function checkUnitOutlier(values, refMass) {
    if (refMass === null || refMass <= 0) return null;
    const gCap = BOUND_G_PER_G * refMass;
    const mgCap = BOUND_MG_PER_G * refMass;
    const ugCap = BOUND_UG_PER_G * refMass;

    for (const code of G_UNIT_NUTRIENTS) {
        if (v(values, code) > gCap) return [KEY_UNIT_OUTLIER, `${code} > ${gCap.toFixed(0)}g — likely unit error`];
    }
    for (const code of MINERALS_MG) {
        if (v(values, code) > mgCap) return [KEY_UNIT_OUTLIER, `${code} > ${mgCap.toFixed(0)}mg — likely unit error`];
    }
    for (const code of MINERALS_UG) {
        if (v(values, code) > ugCap) return [KEY_UNIT_OUTLIER, `${code} > ${ugCap.toFixed(0)}μg — likely unit error`];
    }
    return null;
}

function checkMassBalance(values, refMass) {
    if (refMass === null || refMass <= 0) return null;
    if (refMass < MASS_BALANCE_MIN_MASS_G) return null;
    if (v(values, '255') <= 0 && v(values, '207') <= 0) return null;
    const sum = v(values, '203')
        + v(values, '204')
        + v(values, '205')
        + v(values, '255')
        + v(values, '207')
        + v(values, '221');
    const tol = refMass * MASS_BALANCE_TOL_FRAC;
    if (sum > refMass + tol || sum < refMass - tol) {
        return [KEY_MASS_BALANCE, `Mass balance off: P+F+C+water+ash+alcohol=${sum.toFixed(2)}g vs ref=${refMass.toFixed(2)}g`];
    }
    return null;
}

// ─── Tier 2 rules ───

function checkCarbTree(values) {
    const carbs = v(values, '205');
    if (carbs <= 0) return null;
    const sum = v(values, '269') + v(values, '209') + v(values, '291');
    if (sum > carbs * (1 + TREE_SUM_TOL)) {
        return [KEY_CARB_TREE, `Carb tree: sugars+starch+fiber=${sum.toFixed(2)}g exceeds carbs=${carbs.toFixed(2)}g`];
    }
    return null;
}

function checkSugarComposition(values) {
    const sugars = v(values, '269');
    if (sugars <= 0) return null;
    const components = sumCodes(values, SUGAR_COMPONENTS);
    if (components > sugars * (1 + TREE_SUM_TOL)) {
        return [KEY_SUGAR_COMPOSITION, `Sugar components=${components.toFixed(2)}g exceed total=${sugars.toFixed(2)}g`];
    }
    if (v(values, 'ASG') > sugars * (1 + TREE_SUM_TOL)) {
        return [KEY_SUGAR_COMPOSITION, `Added sugar exceeds total sugars`];
    }
    return null;
}

function checkFiberSplit(values) {
    const fiber = v(values, '291');
    const soluble = v(values, 'SFB');
    const insoluble = v(values, 'IFB');
    if (fiber <= 0 || (soluble <= 0 && insoluble <= 0)) return null;
    const sum = soluble + insoluble;
    if (Math.abs(sum - fiber) / fiber > FIBER_SPLIT_TOL) {
        return [KEY_FIBER_SPLIT, `Soluble+insoluble=${sum.toFixed(2)}g vs fiber=${fiber.toFixed(2)}g`];
    }
    return null;
}

function checkFatTree(values) {
    const fat = v(values, '204');
    if (fat <= 0) return null;
    const sat = v(values, '606');
    const mono = v(values, '645');
    const poly = v(values, '646');
    const trans = v(values, '605');
    const sum = sat + mono + poly + trans;

    if (sum > fat * (1 + TREE_SUM_TOL)) {
        return [KEY_FAT_TREE, `Fat tree exceeds total fat: ${sum.toFixed(2)}g vs ${fat.toFixed(2)}g`];
    }
    if (fat >= FAT_TREE_MIN_PARENT_G
        && sat > 0 && mono > 0 && poly > 0
        && sum < fat * FAT_TREE_MIN_RATIO) {
        return [KEY_FAT_TREE, `Fat tree only ${(sum / fat * 100).toFixed(0)}% of fat`];
    }
    return null;
}

function checkFatSubSum(values, parentCode, components, key, label) {
    const parent = v(values, parentCode);
    if (parent <= 0) return null;
    if (sumCodes(values, components) > parent * (1 + TREE_SUM_TOL)) {
        return [key, `Σ ${label} FAs exceeds ${label}`];
    }
    return null;
}

function checkTransSum(values) {
    const trans = v(values, '605');
    if (trans <= 0) return null;
    if (sumCodes(values, TRANS_COMPONENTS) > trans * (1 + TREE_SUM_TOL)) {
        return [KEY_TRANS_SUM, `Trans-monoenoic+polyenoic exceeds trans fat`];
    }
    return null;
}

function checkOmegaConsistency(values) {
    const dha = v(values, '621');
    const epa = v(values, '629');
    const ala = v(values, '851');
    const alaUndiff = v(values, '619');
    const om3 = v(values, 'OM3');
    const oma = v(values, 'OMA');
    const poly = v(values, '646');

    if (om3 > 0 && (dha > 0 || epa > 0)) {
        const expected = dha + epa;
        if (Math.abs(om3 - expected) > 0.001 + expected * TREE_SUM_TOL) {
            return [KEY_OMEGA_CONSISTENCY, `DHA+EPA vs OM3 mismatch`];
        }
    }
    if (oma > 0 && (dha > 0 || epa > 0 || ala > 0)) {
        const expected = dha + epa + ala;
        if (Math.abs(oma - expected) > 0.001 + expected * TREE_SUM_TOL) {
            return [KEY_OMEGA_CONSISTENCY, `DHA+EPA+ALA vs OMA mismatch`];
        }
    }
    if (ala > 0 && alaUndiff > 0) {
        const diff = Math.abs(ala - alaUndiff) / Math.max(ala, alaUndiff);
        if (diff > TREE_SUM_TOL) {
            return [KEY_OMEGA_CONSISTENCY, `ALA(851) vs alpha-linolenic undiff(619) disagree`];
        }
    }
    if (poly > 0) {
        if (sumCodes(values, OMEGA3_SPECIES) > poly * (1 + TREE_SUM_TOL)) {
            return [KEY_OMEGA_CONSISTENCY, `Σ omega-3 species exceeds PUFA`];
        }
        if (sumCodes(values, OMEGA6_SPECIES) > poly * (1 + TREE_SUM_TOL)) {
            return [KEY_OMEGA_CONSISTENCY, `Σ omega-6 species exceeds PUFA`];
        }
    }
    return null;
}

function checkAminoAcids(values) {
    const protein = v(values, '203');
    if (protein <= 0) return null;
    const adjusted = v(values, '257');
    if (adjusted > protein * (1 + TREE_SUM_TOL)) {
        return [KEY_AMINO_ACIDS, `Adjusted protein exceeds protein`];
    }
    let anyPresent = false;
    for (const code of AMINO_ACIDS) {
        if (Number(values?.[code] ?? 0) > 0) { anyPresent = true; break; }
    }
    if (!anyPresent) return null;
    const sum = sumCodes(values, AMINO_ACIDS);
    if (sum > protein * (1 + TREE_SUM_TOL)) {
        return [KEY_AMINO_ACIDS, `Σ amino acids exceeds protein`];
    }
    let allPresent = true;
    for (const code of AMINO_ACIDS) {
        if (Number(values?.[code] ?? 0) <= 0) { allPresent = false; break; }
    }
    if (allPresent && sum < protein * AMINO_MIN_RATIO) {
        return [KEY_AMINO_ACIDS, `Σ amino acids only ${(sum / protein * 100).toFixed(0)}% of protein`];
    }
    return null;
}

function checkSterolSum(values) {
    const phyto = v(values, '636');
    if (phyto <= 0) return null;
    const sum = v(values, '638') + v(values, '639') + v(values, '641');
    if (sum > phyto * (1 + TREE_SUM_TOL)) {
        return [KEY_STEROL_SUM, `Stigma+campe+β-sito exceeds phytosterols`];
    }
    return null;
}

function checkFodmap(values) {
    const polyols = v(values, 'PLY');
    if (polyols > 0) {
        const sorMan = v(values, 'SOR') + v(values, 'MAN');
        if (sorMan > polyols * (1 + TREE_SUM_TOL)) {
            return [KEY_FODMAP, `Sorbitol+mannitol exceeds total polyols`];
        }
    }
    const fructoseTotal = v(values, '212');
    const fodmapFru = v(values, 'FRC');
    if (fructoseTotal > 0 && fodmapFru > fructoseTotal * (1 + TREE_SUM_TOL)) {
        return [KEY_FODMAP, `FODMAP fructose exceeds total fructose`];
    }
    const lactoseTotal = v(values, '213');
    const fodmapLac = v(values, 'LAC');
    if (lactoseTotal > 0 && fodmapLac > lactoseTotal * (1 + TREE_SUM_TOL)) {
        return [KEY_FODMAP, `FODMAP lactose exceeds total lactose`];
    }
    return null;
}

function checkVitETree(values) {
    const total = v(values, '323');
    if (total <= 0) return null;
    if (v(values, '573') > total * (1 + TREE_SUM_TOL)) {
        return [KEY_VIT_E_TREE, `Vitamin E added exceeds total`];
    }
    if (sumCodes(values, TOCOPHEROL_TOCOTRIENOL) > total * (1 + TREE_SUM_TOL)) {
        return [KEY_VIT_E_TREE, `Σ tocopherols+tocotrienols exceeds Vit E total`];
    }
    return null;
}

function checkFluidsWater(values) {
    const fluids = v(values, 'FLU');
    const water = v(values, '255');
    if (fluids <= 0 || water <= 0) return null;
    if (Math.abs(fluids - water) / Math.max(fluids, water) > FLUIDS_WATER_TOL) {
        return [KEY_FLUIDS_WATER, `Fluids vs water mismatch`];
    }
    return null;
}

function checkServingsHierarchy(values) {
    const veg = v(values, 'VEG');
    const lfy = v(values, 'LFY');
    const svg = v(values, 'SVG');
    const fru = v(values, 'FRU');
    const ber = v(values, 'BER');

    if (veg > 0 && lfy > veg * (1 + TREE_SUM_TOL)) {
        return [KEY_SERVINGS_HIERARCHY, `Leafy greens exceed vegetables`];
    }
    if (veg > 0 && svg > veg * (1 + TREE_SUM_TOL)) {
        return [KEY_SERVINGS_HIERARCHY, `Starchy vegetables exceed vegetables`];
    }
    if (fru > 0 && ber > fru * (1 + TREE_SUM_TOL)) {
        return [KEY_SERVINGS_HIERARCHY, `Berries exceed fruits`];
    }
    return null;
}

function checkMineralsAsh(values) {
    const ash = v(values, '207');
    if (ash <= 0) return null;
    let mineralsMg = 0.0;
    for (const code of MINERALS_MG) mineralsMg += v(values, code);
    for (const code of MINERALS_UG) mineralsMg += v(values, code) / 1000.0;
    const ashMg = ash * 1000.0;
    if (mineralsMg > ashMg * (1 + TREE_SUM_TOL)) {
        return [KEY_MINERALS_ASH, `Σ minerals exceeds ash`];
    }
    return null;
}

// ─── Tier 3: conversion equalities ───

function checkVitDUnits(values) {
    const iu = v(values, '324');
    const mcg = v(values, '328');
    if (iu <= 0 || mcg <= 0) return null;
    const expected = mcg * VIT_D_IU_PER_MCG;
    if (Math.abs(iu - expected) / expected > VIT_D_TOL) {
        return [KEY_VIT_D_UNITS, `Vitamin D unit mismatch`];
    }
    return null;
}

function checkVitDSplit(values) {
    const d2 = v(values, '325');
    const d3 = v(values, '326');
    const total = v(values, '328');
    if (total <= 0 || (d2 <= 0 && d3 <= 0)) return null;
    const sum = d2 + d3;
    if (Math.abs(sum - total) / total > TREE_SUM_TOL) {
        return [KEY_VIT_D_SPLIT, `D2+D3 vs total mismatch`];
    }
    return null;
}

function checkVitAUnits(values) {
    const rae = v(values, '320');
    const iu = v(values, '318');
    const retinol = v(values, '319');
    const betaCar = v(values, '321');
    const alphaCar = v(values, '322');
    const betaCryp = v(values, '334');

    if (rae <= 0 && iu <= 0) return null;
    if (retinol <= 0 && betaCar <= 0 && alphaCar <= 0 && betaCryp <= 0) return null;

    const raeCalc = retinol + betaCar / 12.0 + alphaCar / 24.0 + betaCryp / 24.0;
    const iuCalc = retinol * 3.33 + betaCar * 1.67 + (alphaCar + betaCryp) * 0.83;

    if (rae > 0 && raeCalc > 0) {
        if (Math.abs(rae - raeCalc) / Math.max(rae, raeCalc) > VIT_A_TOL) {
            return [KEY_VIT_A_UNITS, `Vitamin A RAE vs computed mismatch`];
        }
    }
    if (iu > 0 && iuCalc > 0) {
        if (Math.abs(iu - iuCalc) / Math.max(iu, iuCalc) > VIT_A_TOL) {
            return [KEY_VIT_A_UNITS, `Vitamin A IU vs computed mismatch`];
        }
    }
    return null;
}

function checkFolate(values) {
    const folicAcid = v(values, '431');
    const dfe = v(values, '435');
    if (folicAcid > 0 && dfe > 0 && folicAcid > dfe * (1 + TREE_SUM_TOL)) {
        return [KEY_FOLATE, `Folic acid exceeds Folate DFE`];
    }
    return null;
}
